import { parseISO } from 'date-fns'

import { IndividualType } from '@/hooks/useIndividuals'

import { useProcessingDocuments } from './useProcessingDocuments'
import { RegenState } from './useRegenState'

/**
 * Single source of truth for "should we offer to regenerate this individual?" —
 * shared by the home banner and the sidebar's new-data dot, which previously
 * derived it separately and disagreed (the banner treated an explicit
 * `stale: false` as final, so an individual with documents but no generation yet
 * got a dot and no banner).
 *
 * Takes `regen` rather than reading it from context so RegenProvider can call
 * this itself: the provider owns the single app-wide subscription, and the
 * per-document listeners inside `useProcessingDocuments` need sharing the same
 * way. `regen.stale` is the live RTDB value merged over the individual response
 * — reading `stale` off the response alone loses the transition, because the
 * backend mirrors a document's terminal status to RTDB (which triggers our
 * refetch) BEFORE it persists `stale: true`.
 */
export const useRegenOffer = (individual: IndividualType | undefined, regen: RegenState) => {
  const { isProcessing } = useProcessingDocuments(individual)

  const lastRegen = individual?.regen?.lastRegeneratedAt
    ? parseISO(individual.regen.lastRegeneratedAt)
    : null

  const documents = individual?.documents ?? []
  // Documents added since the last run. With no run yet, every document is new.
  const newDocsCount = lastRegen
    ? documents.filter((d) => parseISO(d.createdAt) > lastRegen).length
    : documents.length

  const hasNewData = regen.stale || newDocsCount > 0

  return {
    newDocsCount,
    /**
     * Withheld while the pipeline is still working. A document finishing marks
     * the individual stale, so the offer would appear while its siblings are
     * still parsing — and regenerating then reads a half-ingested corpus and
     * clears `stale` on the way out, so the remaining documents never raise the
     * offer again. The run that lands last re-marks stale and it comes back.
     */
    shouldOffer: hasNewData && !isProcessing,
  }
}
